import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  listAssignablePlanners,
  listSubmissions,
  updateSubmission,
  type Submission,
  type SubmissionSort,
  type SubmissionStatus,
} from "@/api/submissions";

export type SubmissionStatusFilter = SubmissionStatus | "all";
export type SubmissionAssigneeFilter = number | "unassigned" | "all";

const DEFAULT_PER_PAGE = 25;

type UseSubmissionsQueueOptions = {
  municipalityId?: number;
};

export function useSubmissionsQueue({ municipalityId }: UseSubmissionsQueueOptions) {
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<SubmissionStatusFilter>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<SubmissionAssigneeFilter>("all");
  const [sort, setSort] = useState<SubmissionSort>("newest");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Any filter change resets to the first page.
  useEffect(() => {
    setPage(1);
  }, [municipalityId, statusFilter, assigneeFilter, sort, debouncedSearch, perPage]);

  const listQueryKey = useMemo(
    () =>
      [
        "submissions",
        municipalityId,
        statusFilter,
        assigneeFilter,
        sort,
        debouncedSearch,
        page,
        perPage,
      ] as const,
    [municipalityId, statusFilter, assigneeFilter, sort, debouncedSearch, page, perPage],
  );

  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: ({ signal }) =>
      listSubmissions(municipalityId!, {
        status: statusFilter === "all" ? undefined : statusFilter,
        assigned_planner_id: assigneeFilter === "all" ? undefined : assigneeFilter,
        sort,
        search: debouncedSearch || undefined,
        page,
        per_page: perPage,
        signal,
      }),
    enabled: municipalityId != null,
    placeholderData: (prev) => prev,
  });

  const plannersQuery = useQuery({
    queryKey: ["assignable_planners", municipalityId] as const,
    queryFn: ({ signal }) => listAssignablePlanners(municipalityId!, signal),
    enabled: municipalityId != null,
  });

  const invalidate = () => {
    if (municipalityId == null) return;
    queryClient.invalidateQueries({ queryKey: ["submissions", municipalityId] });
  };

  const assignMutation = useMutation({
    mutationFn: ({ id, plannerId }: { id: number; plannerId: number | null }) =>
      updateSubmission(municipalityId!, id, { assigned_planner_id: plannerId }),
    onSuccess: invalidate,
  });

  const submissions: Submission[] = listQuery.data?.submissions ?? [];

  return {
    submissions,
    planners: plannersQuery.data ?? [],
    statusFilter,
    setStatusFilter,
    assigneeFilter,
    setAssigneeFilter,
    sort,
    setSort,
    search,
    setSearch,
    page,
    setPage,
    perPage,
    setPerPage,
    totalCount: listQuery.data?.total_count ?? 0,
    totalPages: listQuery.data?.total_pages ?? 0,
    isLoading: listQuery.isLoading,
    isFetching: listQuery.isFetching,
    error: listQuery.error,
    assigning: assignMutation.isPending,
    assignPlanner: (id: number, plannerId: number | null) =>
      assignMutation.mutate({ id, plannerId }),
    refresh: invalidate,
  };
}

export type UseSubmissionsQueueReturn = ReturnType<typeof useSubmissionsQueue>;
